import { useMemo } from "react";
import { ComposedChart, Bar, Line, Area, XAxis, YAxis, Tooltip, ReferenceLine, ReferenceDot, ResponsiveContainer } from "recharts";
import { useGet } from "@/lib/api";
import { fmt } from "@/lib/format";
import { CandlestickChart } from "lucide-react";

const hhmm = (iso) => new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

function Candle({ x, width, payload, background }) {
  if (!payload || !background) return null;
  const { o, c, h, l } = payload;
  const top = background.y, span = background.height;
  const [dMin, dMax] = payload._dom;
  const y = (v) => top + ((dMax - v) / (dMax - dMin)) * span;
  const up = c >= o;
  const color = up ? "#10B981" : "#EF4444";
  const bodyTop = y(Math.max(o, c)), bodyH = Math.max(1, Math.abs(y(o) - y(c)));
  const cx = x + width / 2, w = Math.max(2, width * 0.6);
  return (
    <g>
      <line x1={cx} x2={cx} y1={y(h)} y2={y(l)} stroke={color} strokeWidth={1} />
      <rect x={cx - w / 2} y={bodyTop} width={w} height={bodyH} fill={color} opacity={0.9} />
    </g>
  );
}

function Tip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="glass rounded-lg px-3 py-2 text-[11px] font-mono-num">
      <div className="text-[#9CA3AF]">{hhmm(p.t)}</div>
      <div>O {fmt(p.o)} H {fmt(p.h)} L {fmt(p.l)} C {fmt(p.c)}</div>
      <div className="text-[#F59E0B]">EMA9 {fmt(p.ema9)}</div>
      {p.dxy_implied != null && <div className="text-[#A78BFA]">DXY-implied {fmt(p.dxy_implied)}</div>}
      <div className="text-[#60A5FA]">EMA120 {fmt(p.ch_lo)}–{fmt(p.ch_hi)}</div>
    </div>
  );
}

export function ScalpChart({ tf, refreshKey }) {
  const { data: d, loading } = useGet(`/technical/chart?tf=${tf}`, `${refreshKey}-${tf}`);

  const { rows, dom, sig } = useMemo(() => {
    if (!d?.bars?.length) return { rows: [], dom: [0, 1], sig: null };
    const sig = d.signal;
    const sl = d.structure_levels || {};
    const lvls = [sig?.entry, sig?.stop_loss, sig?.take_profit, d.bos?.level, d.bos?.swing, sl.up_bos_above, sl.down_bos_below].filter((v) => v != null);
    const implied = d.dxy?.implied || [];
    let lo = Math.min(...d.bars.map((b) => Math.min(b.l, b.ch_lo)), ...lvls, ...implied.filter((v) => v != null));
    let hi = Math.max(...d.bars.map((b) => Math.max(b.h, b.ch_hi)), ...lvls, ...implied.filter((v) => v != null));
    const pad = (hi - lo) * 0.05 || 1;
    const dom = [lo - pad, hi + pad];
    return { rows: d.bars.map((b, i) => ({ ...b, range: [b.l, b.h], channel: [b.ch_lo, b.ch_hi], dxy_implied: implied[i] ?? null, _dom: dom })), dom, sig };
  }, [d, tf]);

  if (loading && !d) return <section className="glass rounded-3xl p-6 text-center text-[#6B7280] font-mono-num text-sm">Loading {tf} chart…</section>;
  if (!d || d.error || d.warming_up) return null;

  const locked = !d.is_pro;
  const marks = (d.signals || []).filter((s) => rows.some((r) => r.t === s.time));
  const sl = d.structure_levels || {};
  const dx = d.dxy;
  const biasColor = dx?.bias === "UP" ? "#10B981" : dx?.bias === "DOWN" ? "#EF4444" : "#9CA3AF";

  return (
    <section data-testid="scalp-chart" className="glass rounded-3xl p-4 flex flex-col gap-2">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <CandlestickChart size={15} className="text-[#F59E0B]" />
          <h3 className="font-display text-sm font-bold">XAUUSD {tf.toUpperCase()} · last {rows.length} candles</h3>
        </div>
        <div className="flex items-center gap-3 text-[10px] font-mono-num text-[#9CA3AF] flex-wrap">
          <span><i className="inline-block w-3 h-0.5 bg-[#F59E0B] align-middle mr-1" />EMA9</span>
          <span><i className="inline-block w-3 h-2 bg-[#60A5FA]/30 align-middle mr-1" />EMA120 channel</span>
          {sl.up_bos_above != null && <span><i className="inline-block w-3 h-0.5 border-t border-dashed border-[#10B981] align-middle mr-1" />Up BOS above</span>}
          {sl.down_bos_below != null && <span><i className="inline-block w-3 h-0.5 border-t border-dashed border-[#EF4444] align-middle mr-1" />Down BOS below</span>}
          {dx && <span><i className="inline-block w-3 h-0.5 border-t-2 border-dotted border-[#A78BFA] align-middle mr-1" />DXY-implied gold</span>}
        </div>
      </div>
      {dx && (
        <div data-testid="dxy-bias" className="flex items-start gap-2 rounded-xl px-3 py-2 text-[11px] leading-snug" style={{ background: `${biasColor}14`, border: `1px solid ${biasColor}44` }}>
          <span className="font-display font-extrabold text-xs shrink-0" style={{ color: biasColor }}>Dollar says: gold {dx.bias === "UP" ? "↑ UP" : dx.bias === "DOWN" ? "↓ DOWN" : "— neutral"}</span>
          <span className="text-[#9CA3AF]">{dx.reason}</span>
        </div>
      )}
      <div className="h-64 sm:h-80 -mx-2">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: 8, right: 56, bottom: 0, left: 0 }}>
            <XAxis dataKey="t" tickFormatter={hhmm} tick={{ fill: "#6B7280", fontSize: 10 }} minTickGap={40} axisLine={false} tickLine={false} />
            <YAxis domain={dom} orientation="right" tick={{ fill: "#6B7280", fontSize: 10 }} tickFormatter={(v) => fmt(v)} width={56} axisLine={false} tickLine={false} />
            <Tooltip content={<Tip />} cursor={{ stroke: "#ffffff22" }} />
            <Area type="monotone" dataKey="channel" stroke="#60A5FA" strokeWidth={1} fill="#60A5FA" fillOpacity={0.14} dot={false} isAnimationActive={false} />
            <Bar dataKey="range" shape={<Candle />} isAnimationActive={false} />
            <Line type="monotone" dataKey="ema9" stroke="#F59E0B" strokeWidth={1.5} dot={false} isAnimationActive={false} />
            {dx && <Line type="monotone" dataKey="dxy_implied" stroke="#A78BFA" strokeWidth={1.5} strokeDasharray="2 3" dot={false} connectNulls isAnimationActive={false} />}
            {d.bos && <ReferenceLine y={d.bos.level} stroke="#D1D5DB" strokeDasharray="4 4" label={{ value: `${d.bos.direction === "DOWN" ? "Down" : "Up"} BOS ${fmt(d.bos.level)} (broken)`, fill: "#D1D5DB", fontSize: 10, position: "insideTopLeft" }} />}
            {sl.up_bos_above != null && <ReferenceLine y={sl.up_bos_above} stroke="#10B981" strokeDasharray="3 3" label={{ value: `${sl.up_broken ? "Up BOS ✓ " : "Up BOS above "}${fmt(sl.up_bos_above)}`, fill: "#10B981", fontSize: 10, position: "insideTopRight" }} />}
            {sl.down_bos_below != null && <ReferenceLine y={sl.down_bos_below} stroke="#EF4444" strokeDasharray="3 3" label={{ value: `${sl.down_broken ? "Down BOS ✓ " : "Down BOS below "}${fmt(sl.down_bos_below)}`, fill: "#EF4444", fontSize: 10, position: "insideBottomRight" }} />}
            {sig?.entry != null && !locked && <>
              <ReferenceLine y={sig.entry} stroke="#F59E0B" label={{ value: `Entry ${fmt(sig.entry)}`, fill: "#F59E0B", fontSize: 10, position: "insideLeft" }} />
              <ReferenceLine y={sig.stop_loss} stroke="#EF4444" label={{ value: `SL ${fmt(sig.stop_loss)}`, fill: "#EF4444", fontSize: 10, position: "insideLeft" }} />
              <ReferenceLine y={sig.take_profit} stroke="#10B981" label={{ value: `TP ${fmt(sig.take_profit)}`, fill: "#10B981", fontSize: 10, position: "insideLeft" }} />
            </>}
            {marks.map((s) => (
              <ReferenceDot key={s.id} x={s.time} y={rows.find((r) => r.t === s.time)?.[s.direction === "LONG" ? "l" : "h"]} r={5}
                fill={s.direction === "LONG" ? "#10B981" : "#EF4444"} stroke="none" strokeWidth={2} />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {locked && sig && <p data-testid="chart-locked-hint" className="text-[10px] text-[#F59E0B]/80 font-mono-num">Entry / SL / TP lines are a Pro feature.</p>}
    </section>
  );
}
